import type { ReactNode } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { AppLoaderPage } from '@gokrew/ui/components/AppLoaderPage';
import { useMyTeamAccess } from '@/hooks/my-team/useMyTeam';
import { useRouteWithPermission } from '@/hooks/useRouteWithPermission';
import { MyTeamAccessLost } from '../MyTeamAccessLost';

export const MY_TEAM_PATH = '/my-team';
export const MY_TEAM_APPROVALS_PATH = '/my-team/approvals';

/**
 * Guards the manager screens of My team (People, Team calendar, Out of
 * office, a member's page). My team itself is open to every employee (D49):
 * its Team tab needs no guard. `allowDelegate` also admits someone who covers
 * a manager's approvals right now (the Approvals inbox, D10).
 *
 * Anyone else is sent to their own Team tab, except when they follow an old
 * approval link (`?request=`): `MyTeamAccessLost` then says the request is no
 * longer theirs to decide, rather than dropping them without a word. The
 * server refuses every manager call for them anyway (403).
 */
export const MyTeamGuard = ({
  children,
  allowDelegate = false,
}: {
  children: ReactNode;
  allowDelegate?: boolean;
}) => {
  const { data, isPending, isError } = useMyTeamAccess();
  const [searchParams] = useSearchParams();
  const fallback = useRouteWithPermission();

  // `isPending`, not `isLoading`: the probe waits for the profile, and a
  // query that has not started yet is pending but not loading. Deciding then
  // would bounce a manager away on a fresh page load.
  if (isPending && !isError) return <AppLoaderPage />;
  const isManager = !isError && !!data?.enabled && !!data?.isManager;
  const isDelegate = !isError && !!data?.enabled && !!data?.isDelegate;
  if (isManager || (allowDelegate && isDelegate)) return <>{children}</>;
  if (searchParams.has('request')) {
    return isDelegate ? (
      <Navigate to={`${MY_TEAM_APPROVALS_PATH}?${searchParams}`} replace />
    ) : (
      <MyTeamAccessLost fallback={fallback} />
    );
  }
  return <Navigate to={MY_TEAM_PATH} replace />;
};
